import React, { useEffect, useState } from "react";
import "./VersionHistory.scss";
import { API_BASE_URL } from '../../config';
import { authHeaders } from '../../utils/api';

function formatStamp(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('ar-EG', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function relativeShort(iso) {
    if (!iso) return '';
    const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (seconds < 60) return `قبل ${seconds} ث`;
    const min = Math.floor(seconds / 60);
    if (min < 60) return `قبل ${min} د`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `قبل ${hr} س`;
    return `قبل ${Math.floor(hr / 24)} ي`;
}

export default function VersionHistory({
    open, onClose, bookId, authTokens, onRestored,
}) {

    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [previewing, setPreviewing] = useState(null); // { id, snapshot } | null
    const [confirmRestore, setConfirmRestore] = useState(null);
    const [error, setError] = useState(null);
    const [newLabel, setNewLabel] = useState('');

    const reload = async () => {
        if (!bookId) return;
        setLoading(true); setError(null);
        try {
            const r = await fetch(`${API_BASE_URL}/api/books/versions/?book=${bookId}`, {
                headers: authHeaders(authTokens),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const data = await r.json();
            setVersions(data.versions || []);
        } catch {
            setError('تعذر تحميل سجل الإصدارات');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && bookId) {
            reload();
        } else {
            setPreviewing(null);
            setConfirmRestore(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, bookId]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const saveNewVersion = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/books/versions/`, {
                method: 'POST',
                headers: { ...authHeaders(authTokens), 'Content-Type': 'application/json' },
                body: JSON.stringify({ book: bookId, label: newLabel.trim() }),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            setNewLabel('');
            await reload();
        } catch {
            setError('تعذر حفظ الإصدار');
        } finally {
            setBusy(false);
        }
    };

    const openPreview = async (versionId) => {
        setBusy(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/books/versions/${versionId}/`, {
                headers: authHeaders(authTokens),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            setPreviewing(await r.json());
        } catch {
            setError('تعذر تحميل الإصدار');
        } finally {
            setBusy(false);
        }
    };

    const doRestore = async () => {
        if (!confirmRestore) return;
        setBusy(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/books/versions/${confirmRestore.id}/restore/`, {
                method: 'POST',
                headers: authHeaders(authTokens),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            setConfirmRestore(null);
            setPreviewing(null);
            onRestored?.();
            onClose();
        } catch {
            setError('تعذر الاستعادة');
        } finally {
            setBusy(false);
        }
    };

    const doDelete = async (versionId) => {
        setBusy(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/books/versions/${versionId}/`, {
                method: 'DELETE',
                headers: authHeaders(authTokens),
            });
            if (!r.ok && r.status !== 204) throw new Error(`HTTP ${r.status}`);
            await reload();
        } catch {
            setError('تعذر الحذف');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className='vh-overlay' onClick={onClose} role='dialog' aria-modal='true' aria-label='سجل الإصدارات'>
            <div className='vh-panel' onClick={(e) => e.stopPropagation()}>
                <div className='vh-header'>
                    <h2>سجل الإصدارات</h2>
                    <button className='vh-close' onClick={onClose} aria-label='إغلاق'>×</button>
                </div>

                {!bookId && (
                    <p className='vh-empty'>احفظ الكتاب أولًا لإنشاء إصدارات.</p>
                )}

                {bookId && (
                    <>
                        <div className='vh-create'>
                            <input
                                type='text'
                                value={newLabel}
                                onChange={(e) => setNewLabel(e.target.value)}
                                placeholder='اسم الإصدار (اختياري)'
                                maxLength={120}
                                disabled={busy}
                            />
                            <button onClick={saveNewVersion} disabled={busy}>
                                {busy ? '...' : '+ احفظ إصدارًا'}
                            </button>
                        </div>

                        {error && <p className='vh-error'>{error}</p>}

                        <div className='vh-body'>
                            <ul className='vh-list'>
                                {loading && <li className='vh-loading'>جارٍ التحميل…</li>}
                                {!loading && versions.length === 0 && (
                                    <li className='vh-empty'>لا توجد إصدارات بعد.</li>
                                )}
                                {versions.map(v => (
                                    <li
                                        key={v.id}
                                        className={'vh-item' + (previewing?.id === v.id ? ' is-selected' : '')}
                                    >
                                        <button
                                            type='button'
                                            className='vh-item-main'
                                            onClick={() => openPreview(v.id)}
                                        >
                                            <div className='vh-item-title'>
                                                {v.label || (v.is_auto ? 'حفظ تلقائي' : 'إصدار يدوي')}
                                                {!v.is_auto && <span className='vh-tag'>يدوي</span>}
                                            </div>
                                            <div className='vh-item-meta'>
                                                <span title={formatStamp(v.created_at)}>{relativeShort(v.created_at)}</span>
                                                {' · '}
                                                <span>{v.chapter_count} {v.chapter_count === 1 ? 'فصل' : 'فصول'}</span>
                                                {' · '}
                                                <span>{v.word_count.toLocaleString('ar-EG')} كلمة</span>
                                            </div>
                                        </button>
                                        <button
                                            type='button'
                                            className='vh-item-delete'
                                            onClick={() => doDelete(v.id)}
                                            disabled={busy}
                                            aria-label='حذف الإصدار'
                                            title='حذف'
                                        >×</button>
                                    </li>
                                ))}
                            </ul>

                            <div className='vh-preview'>
                                {previewing ? (
                                    <>
                                        <header className='vh-preview-header'>
                                            <h3>{previewing.snapshot?.title || 'بدون عنوان'}</h3>
                                            <small>{formatStamp(previewing.created_at)}</small>
                                            {previewing.label && <small className='vh-preview-label'>{previewing.label}</small>}
                                        </header>
                                        {previewing.snapshot?.description && (
                                            <p className='vh-preview-desc'>{previewing.snapshot.description}</p>
                                        )}
                                        <ol className='vh-preview-chapters'>
                                            {(previewing.snapshot?.chapters || []).map((c) => (
                                                <li key={c.order}>
                                                    <span className='vh-preview-ch-num'>{c.order + 1}</span>
                                                    <span>{c.title || `الفصل ${c.order + 1}`}</span>
                                                </li>
                                            ))}
                                        </ol>
                                        <button
                                            className='vh-restore'
                                            disabled={busy}
                                            onClick={() => setConfirmRestore(previewing)}
                                        >
                                            استعد هذا الإصدار
                                        </button>
                                    </>
                                ) : (
                                    <p className='vh-preview-empty'>اختر إصدارًا لمعاينته.</p>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {confirmRestore && (
                <div className='vh-confirm' onClick={(e) => e.stopPropagation()}>
                    <div className='vh-confirm-box'>
                        <h3>استعادة الإصدار؟</h3>
                        <p>
                            سيُستبدل المحتوى الحالي بمحتوى هذا الإصدار. سيتم حفظ نسخة من الحالة الحالية أوّلاً، فيمكنك التراجع لاحقًا.
                        </p>
                        <div className='vh-confirm-actions'>
                            <button onClick={() => setConfirmRestore(null)} disabled={busy}>إلغاء</button>
                            <button className='vh-confirm-yes' onClick={doRestore} disabled={busy}>
                                {busy ? 'جارٍ الاستعادة…' : 'استعد'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
